import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Alert, Button, Pagination, Radio, Row, Select, Space, Tabs } from 'antd';
import useFetchListData from '../../hooks/useFetchListData';
import HighlightedTable from '../../components/Tables/HighlightedTable';
import Search from 'antd/es/input/Search';
import useNotification from '@/hooks/notifications';
import { highlightItemPost } from '../../utils/fetchFunctions/highlight';

const items = [
	{
			key: 'true',
			label: 'SELECTED MEDIA',
	},
	{
			key: 'false',
			label: 'ALL MEDIA',
	},
];

const searchTypes = [
	{ label: 'Search by reference ID', value: 'epId' },
	{ label: 'Search by keywords', value: 'search' },
];

const timeOut = 500;

export default function RelevantMedia () {
	const {openNotification} = useNotification();
	const [loadingData, setLoadingData] = useState(false);
	const [showInfoMessage, setShowInfoMessage] = useState(false);
	const fetchTimeout = useRef(null);

	const params = useMemo(() => {
		return {
				type: 'video',
				sort: 'mediaDate:desc',
				mostRelevant: true,
				pageSize: 12,
		};
	}, []);

	const {setParams, data, loading, searchParams, setParam, getData, error, pagination } = useFetchListData({params, addS: true});

	useEffect(() => {
		if (error) {
			openNotification({message: 'Something went wrong!', description: error.message || null, type: 'error'});
		}
	}, [error]);

	const handleSelected = (value) => {
		setParams({
			mostRelevant: value,
			page: null,
			pageSize: null,
			search: null
		});
	};

	const handleType = ({ target: { value } }) => {
		setParams({type: value, page: null, pageSize: null, search: null});
	};


	const handlePagination = (page, pageSize) => {
		setParams({page, pageSize});
	};

	const handleSearch = () => {
		setParams({sort: 'mediaDate:desc'});
	};

	async function select (id) {
		setLoadingData(true);
		const url = searchParams.get("type") || 'video';
		const res = await highlightItemPost({url, id, highlighted: true, name: 'relevant'});

		if (res.error) {
			openNotification({message: 'Something went wrong!', description: res.message || null, type: 'error'});
			setLoadingData(false);
			return;
		}

		clearTimeout(fetchTimeout.current);
		fetchTimeout.current = setTimeout(async () => {
			await getData();
			setLoadingData(false);
			setShowInfoMessage(true);
		}, timeOut);
	}

	async function unselect (id) {
		setLoadingData(true);
		const url = searchParams.get("type") || 'video';
		const res = await highlightItemPost({url, id, highlighted: false, name: 'relevant'});

		if (res.error) {
			openNotification({message: 'Something went wrong!', description: res.message || null, type: 'error'});
			setLoadingData(false);
			return;
		}

		clearTimeout(fetchTimeout.current);
		fetchTimeout.current = setTimeout(async () => {
			await getData();
			setLoadingData(false);
			setShowInfoMessage(true);
		}, timeOut);
	}

	const isAllMedia = useMemo(() => {
		return searchParams.get("mostRelevant") === 'false';
	}, [searchParams.get("mostRelevant")]);

	return (
		<Space size="large" direction='vertical' style={{width: '100%'}} >
			<h1>{`Homepage > Relevant Media`}</h1>
			<Alert
					description={
						<>For the <b>All Media</b> tab, a mix of selected items (up to 12 items), such as videos or photosets, will be displayed in the order of selection.<br/>
						For the <b>Videos</b> and <b>Photosets</b> tabs, selected items of these types of media (up to 12 items) will be displayed in the order of selection.</>}
					type="info"
					style={{marginBottom: 20}}
			/>
			{
				showInfoMessage && (
					<Alert
						description={
							<Space size="large">
								<p>{"If you don't see the update after selecting a media, we recommend refreshing the page because the selected media is being indexed."}</p>
								<Button type="primary" key="back" onClick={getData}>
									Refresh
								</Button>
							</Space>
						}
						type="info"
					/>
				)
			}
			<Tabs
					defaultActiveKey="selected"
					items={items}
					size="large"
					onChange={handleSelected}
					activeKey={searchParams.get("mostRelevant") || 'true'}
			/>
			<Radio.Group
					options={[
						{
							label: 'Videos',
							value: 'video',
						},
						{
							label: 'Photosets',
							value: 'photoset',
						},
					]}
					onChange={handleType}
					value={searchParams.get("type") || 'video'}
					optionType="button"
					size="large"
			/>
			{searchParams.get("mostRelevant") === 'false' && (
				<Search
					addonBefore={
						<Select
							style={{width: 190}}
							options={searchTypes}
							value={searchParams.get("searchType") || 'search'}
							onChange={setParam('searchType')}
						/>
					}
					value={searchParams.get("search") || ''}
					onChange={setParam('search', false)}
					onSearch={handleSearch}
					enterButton
				/>
			)}
			<HighlightedTable
				data={data}
				selectedKey="mostRelevant"
				type={searchParams.get("type") || 'audio'}
				position={!isAllMedia}
				sort={isAllMedia ? searchParams.get("sort") || 'mediaDate:desc' : null}
				setSort={setParam('sort')}
				select={select}
				unselect={unselect}
				loading={loading || loadingData}
				selectKey="epId"
			/>
			<Row justify="center">
					<Pagination
						total={pagination.totalElements}
						pageSize={pagination.pageSize}
						current={pagination.page}
						showSizeChanger
						showQuickJumper
						hideOnSinglePage
						disabled={loading}
						onChange={handlePagination}
					/>
				</Row>
		</Space>
	);
}